'use client';

import {
  Box,
  Avatar,
  Divider,
  Typography,
  IconButton,
  SystemIcon,
} from '@wishket/design-system';

import { useRef } from 'react';
import { useClickOutside } from '@wishket/yogokit';

import { formatViewCount } from '@/shared/utils';

interface MoreMenuProps {
  onEditCollection: () => void;
  onDeleteCollection: () => void;
}

interface MenuListProps extends MoreMenuProps {
  isMoreOptionsOpen: boolean;
  onMenuClick: () => void;
}

interface AuthorInfoProps {
  name: string;
  image: string;
  roleKor: string;
}

interface CollectionCountSectionProps {
  viewCount: number;
  likeCount: number;
  scrapCount: number;
}

export const CollectionCountSection = ({
  viewCount,
  likeCount,
  scrapCount,
}: CollectionCountSectionProps) => {
  return (
    <Box className="flex items-center gap-3">
      <Box className="flex h-5 items-center gap-1.5">
        <SystemIcon solid name="small_eye" className="text-w-gray-600" />
        <Typography variant="body3" className="font-medium text-w-gray-600">
          {formatViewCount(viewCount)}
        </Typography>
      </Box>
      <Box className="flex h-5 items-center gap-1.5">
        <SystemIcon solid name="small_like" className="text-w-gray-600" />
        <Typography variant="body3" className="font-medium text-w-gray-600">
          {formatViewCount(likeCount)}
        </Typography>
      </Box>
      <Box className="flex h-5 items-center gap-1.5">
        <SystemIcon solid name="small_scrap" className="text-w-gray-600" />
        <Typography variant="body3" className="font-medium text-w-gray-600">
          {formatViewCount(scrapCount)}
        </Typography>
      </Box>
    </Box>
  );
};

export const AIRecommendSection = ({ onClick }: { onClick?: () => void }) => {
  return (
    <Box className="flex items-center gap-4 bg-primary-10 px-8 py-4 tablet:rounded-b-3xl">
      <SystemIcon name="medium_magic" className="text-primary-500" />
      <Typography
        variant="body1"
        className="font-medium text-w-gray-900"
        data-testid="ai-recommend-text"
      >
        AI로{' '}
        <span
          onClick={onClick}
          className="cursor-pointer text-primary desktop:hover:underline"
        >
          컬렉션 표지
        </span>
        를 만들어 보세요!
      </Typography>
    </Box>
  );
};

export const MenuList = ({
  isMoreOptionsOpen,
  onMenuClick,
  onEditCollection,
  onDeleteCollection,
}: MenuListProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => {
    if (isMoreOptionsOpen) {
      onMenuClick();
    }
  });

  const handleMenuClick = (action: 'edit' | 'delete') => {
    switch (action) {
      case 'edit':
        onEditCollection();
        break;
      case 'delete':
        onDeleteCollection();
        break;
    }

    onMenuClick();
  };

  return (
    <Box className="relative flex items-center gap-1" ref={ref}>
      <IconButton onClick={onMenuClick}>
        <SystemIcon name="medium_more_options" />
      </IconButton>
      {isMoreOptionsOpen && (
        <Box className="absolute right-0 top-full mt-2 rounded-xl bg-white p-2 shadow-graymedium">
          <MoreMenu
            onEditCollection={() => handleMenuClick('edit')}
            onDeleteCollection={() => handleMenuClick('delete')}
          />
        </Box>
      )}
    </Box>
  );
};

export const MoreMenu = ({
  onEditCollection,
  onDeleteCollection,
}: MoreMenuProps) => {
  return (
    <Box className="flex w-[160px] flex-col rounded-xl bg-white">
      <Box
        onClick={onEditCollection}
        className="flex cursor-pointer items-center gap-1.5 px-4 py-3"
      >
        <SystemIcon name="medium_edit" className="text-w-gray-600" />
        <Typography variant="body1" className="text-w-gray-600">
          컬렉션 수정
        </Typography>
      </Box>
      <Box
        onClick={onDeleteCollection}
        className="flex cursor-pointer items-center gap-1.5 px-4 py-3"
      >
        <SystemIcon name="medium_trash" className="text-w-gray-600" />
        <Typography variant="body1" className="text-w-gray-600">
          삭제하기
        </Typography>
      </Box>
    </Box>
  );
};

export const AuthorInfo = ({ name, image, roleKor }: AuthorInfoProps) => {
  return (
    <Box className="mb-1.5 flex items-center gap-2">
      <Avatar size="sm" imgUrl={image} />
      <Typography variant="body2" className="line-clamp-1 text-w-gray-700">
        {name}
      </Typography>
      <Divider isVertical className="h-3.5" />
      <Typography variant="body2" className="line-clamp-1 text-w-gray-700">
        {roleKor}
      </Typography>
    </Box>
  );
};
